import {
    countRequestsByType,
    requestBackgroundCheckScreening,
    requireManageAccountsAndAnySystemPermission,
} from '@/backend/features/background-check';
import {
    BackgroundCheckColumns,
    BackgroundCheckData,
    backgroundCheckColumns,
} from '@/backend/features/background-check/shared/entities/background-check/types';
import {
    getBackgroundCheckConfig,
    getBackgroundCheckQuotaConfig,
    getBackgroundCheckTestConfig,
} from '@/backend/features/background-check/utils/getBackgroundCheckConfigForType';
import { getBackgroundCheckStorage } from '@/backend/features/background-check/utils/getBackgroundCheckStorage';
import { getExtendedAccountStorage } from '@/backend/features/contact-management/utils/getExtendedAccountStorage';
import { getReadonlyExtendedAccountStorage } from '@/backend/features/contact-management/utils/getReadonlyExtendedAccountStorage';
import { getAccountStorage } from '@juvare/contact-management/backend/features/accounts';
import { requireAnySystemPermission } from '@juvare/contact-management/backend/features/permissions/shared';
import { administratorAccountEmail } from '@juvare/credentialing/backend/constants';
import { AuthError, defineEntity } from '@juvare/platform-app/App';
import { sql } from '@juvare/platform-app/Integration';
import { AppState, UserState } from '@juvare/platform-sdk-react/types';
import { createBackgroundCheckHistory } from '../../background-tasks';
import { BackgroundCheckStatus, BackgroundCheckType } from '../../types';
import {
    BackgroundCheckHistoryColumns,
    BackgroundCheckHistoryData,
    backgroundCheckHistoryEntityName,
} from '../background-check-history-data';
import { recalculateBackgroundCheckStatus } from './utils';

export const backgroundCheckEntity = defineEntity<BackgroundCheckData, BackgroundCheckColumns, AppState, UserState>({
    version: 5,
    columns: backgroundCheckColumns,
    migrations: {
        'v1:2025-05-29:initial': ({ dbClient }) => {
            // Create cache columns
            dbClient.update(sql`ALTER TABLE background_check_cache
                ADD COLUMN "account_id" UUID NOT NULL`);
            dbClient.update(
                sql`CREATE INDEX IF NOT EXISTS "ix_background_check_cache_account_id" ON background_check_cache ("account_id")`,
            );

            dbClient.update(sql`ALTER TABLE background_check_cache
                ADD COLUMN "status" TEXT NOT NULL`);
            dbClient.update(sql`CREATE INDEX IF NOT EXISTS "ix_background_check_cache_status" ON background_check_cache (LOWER("status"))`);

            dbClient.update(sql`ALTER TABLE background_check_cache
                ADD COLUMN "type" TEXT NOT NULL`);
            dbClient.update(sql`CREATE INDEX IF NOT EXISTS "ix_background_check_cache_type" ON background_check_cache (LOWER("type"))`);

            dbClient.update(sql`ALTER TABLE background_check_cache
                ADD COLUMN "updated_at" DOUBLE PRECISION NOT NULL`);
            dbClient.update(
                sql`CREATE INDEX IF NOT EXISTS "ix_background_check_cache_updated_at" ON background_check_cache ("updated_at")`,
            );

            dbClient.update(sql`ALTER TABLE background_check_cache
                ADD COLUMN "consent" BOOLEAN NOT NULL`);
            dbClient.update(sql`CREATE INDEX IF NOT EXISTS "ix_background_check_cache_consent" ON background_check_cache ("consent")`);

            // Populate the data
            dbClient.update(sql`
                INSERT INTO background_check_data (_id,
                                                   _updated_by,
                                                   _updated,
                                                   _created_by,
                                                   _created,
                                                   _json)
                SELECT gen_random_uuid(), -- generates a UUID for _id
                       acc._updated_by,
                       acc._updated,
                       acc._created_by,
                       acc._created,
                       jsonb_build_object(
                               'accountId', acc._id,
                               'type', bc.key,
                               'status', bc.value -> 'status',
                               'updatedAt', bc.value -> 'updatedAt',
                               'consent', COALESCE((acc._json ->> 'consentBackgroundCheck')::boolean, false)
                       )
                FROM account_data acc,
                     jsonb_each(acc._json -> 'backgroundCheck') AS bc(key, value);
            `);

            // Populate the cache columns
            dbClient.update(sql`
                INSERT INTO background_check_cache (_id,
                                                    _updated_by,
                                                    _updated,
                                                    _created_by,
                                                    _created,
                                                    account_id,
                                                    type,
                                                    status,
                                                    updated_at,
                                                    consent)
                SELECT bcd._id,
                       bcd._updated_by,
                       bcd._updated,
                       bcd._created_by,
                       bcd._created,
                       (bcd._json ->> 'accountId')::uuid,
                       bcd._json ->> 'type',
                       bcd._json ->> 'status',
                       (bcd._json ->> 'updatedAt')::bigint,
                       (bcd._json ->> 'consent')::bool
                FROM background_check_data bcd
            `);
        },
        'v2:2025-09-08:empty': () => {
            // Required due to incorrect init version
        },
        'v3:2025-09-08:empty': () => {
            // Required due to incorrect init version
        },
        'v4:2025-09-08:add requestedBy column': ({ dbClient }) => {
            dbClient.update(sql`ALTER TABLE background_check_cache
                ADD COLUMN "requested_by" TEXT`);

            dbClient.update(sql`ALTER TABLE background_check_cache
                ADD CONSTRAINT fk_account_id FOREIGN KEY (account_id) REFERENCES account_cache (_id)`);
        },
        'v5:2025-10-16:fix migrations': ({ dbClient }) => {
            // drop incorrectly named fk
            dbClient.update(sql`ALTER TABLE background_check_cache DROP CONSTRAINT IF EXISTS fk_account_id`);

            // create new fk
            dbClient.update(
                sql`CREATE INDEX IF NOT EXISTS "ix_background_check_cache_account_id" ON background_check_cache ("account_id")`,
            );
            dbClient.update(
                sql`ALTER TABLE background_check_cache ADD CONSTRAINT "fk_background_check_account_id" FOREIGN KEY ("account_id") REFERENCES account_cache (_id) ON DELETE CASCADE`,
            );
            dbClient.update(
                sql`CREATE OR REPLACE TRIGGER cascade_data_table_delete AFTER DELETE ON background_check_cache FOR EACH ROW EXECUTE FUNCTION platform.cascade_data_table_delete();`,
            );
        },
    },
    onScheduleChangedAsync: (context, params) => {
        const accountStorage = getReadonlyExtendedAccountStorage(context);

        const accountData =
            params.action === 'create' || params.action === 'update' ? accountStorage.get(params.data.accountId) : undefined;

        if (params.action === 'update' && accountData?.columns.systemStatus === 'deleted') {
            return true;
        }

        if (params.action === 'update' && accountData) {
            return params.data.status === 'completeClear' || params.data.status === 'readyToSubmit';
        }

        return false;
    },
    onChangedAsync: (context, params) => {
        const accountStorage = getAccountStorage(context);
        const backgroundCheckStorage = getBackgroundCheckStorage(context);

        const accountData =
            params.action === 'create' || params.action === 'update' ? accountStorage.get(params.data.accountId) : undefined;

        if (params.action === 'update' && accountData?.columns.systemStatus === 'deleted') {
            try {
                const historyStorage = context.storage<BackgroundCheckHistoryData, BackgroundCheckHistoryColumns>(
                    backgroundCheckHistoryEntityName,
                );

                const historyRows = historyStorage.list({
                    filter: [
                        {
                            column: 'checkResponseByRequestId',
                            op: 'is-not-null',
                        },
                        {
                            column: 'accountId',
                            op: '=',
                            value: accountData?.id,
                        },
                    ],
                }).rows;

                if (historyRows.length !== 0) {
                    historyRows.forEach((historyRow) => {
                        createBackgroundCheckHistory(context, {
                            requestedBy: params.data.requestedBy ?? 'System',
                            type: params.data.type as BackgroundCheckType,
                            accountId: historyRow.columns.accountId,
                            backgroundCheckStatus: params.data.status,
                            nextStatus: 'processingError',
                            message: 'User deleted',
                            stopCheckingForResultsHistoryId: historyRow.id,
                        });
                    });
                }
            } catch (e) {
                console.log('Unknown error occurred while updating history to stop background check task');
            }
        }

        if (
            params.action === 'update' &&
            accountData &&
            (params.data.status === 'readyToSubmit' || params.data.status === 'completeClear')
        ) {
            const config = getBackgroundCheckConfig(context);
            const backgroundCheckTestConfig = getBackgroundCheckTestConfig(context);
            const quota = getBackgroundCheckQuotaConfig(context);
            const isTestMode = Boolean(backgroundCheckTestConfig?.useTestData && backgroundCheckTestConfig?.testData);

            if (params.data.status === 'completeClear') {
                const triggerOtherBackgroundCheck = config?.type?.[params.data.type]?.triggerOtherBackgroundChecksOnSuccess;

                const accountBcs = backgroundCheckStorage.list({
                    filter: [
                        {
                            column: 'accountId',
                            op: '=',
                            value: params.data.accountId,
                        },
                    ],
                });

                // TODO CRMS-3495 check quota, enabled, status parent, status self, ...
                if (triggerOtherBackgroundCheck?.length && config && quota && backgroundCheckTestConfig) {
                    triggerOtherBackgroundCheck.forEach((bcType) => {
                        const validBackgroundCheckToTrigger = accountBcs.rows.find(
                            (backgroundCheck) =>
                                backgroundCheck.columns.status === 'readyToSubmit' && backgroundCheck.columns.type === bcType,
                        );

                        if (validBackgroundCheckToTrigger) {
                            const quotaByType = quota[validBackgroundCheckToTrigger.columns.type as BackgroundCheckType];
                            const validQuotaCount =
                                quotaByType.quota >
                                countRequestsByType(context, validBackgroundCheckToTrigger.columns.type as BackgroundCheckType);

                            if (!quotaByType.enabled || (quotaByType.enabled && validQuotaCount)) {
                                requestBackgroundCheckScreening(context, {
                                    requestedBy: params.data.requestedBy ?? 'System',
                                    accountId: params.data.accountId,
                                    accountData: accountData.data,
                                    backgroundCheckType: validBackgroundCheckToTrigger.columns.type as BackgroundCheckType,
                                    config,
                                    backgroundCheckTestConfig,
                                    isTestMode,
                                    backgroundCheck: validBackgroundCheckToTrigger.columns,
                                    quota,
                                });
                            }
                        }
                    });
                }
            } else if (params.data.status === 'readyToSubmit') {
                const backgroundCheckTypesThatCanTriggerThisType = Object.entries(config?.type || {})
                    .filter(([, configByType]) => {
                        return configByType.triggerOtherBackgroundChecksOnSuccess.some(
                            (typeToTrigger) => typeToTrigger === params.data.type && configByType.enabled,
                        );
                    })
                    .map(([type]) => type);

                const accountBcs = backgroundCheckStorage.list({
                    filter: [
                        {
                            column: 'accountId',
                            op: '=',
                            value: params.data.accountId,
                        },
                    ],
                });

                // TODO CRMS-3495 check quota, enabled, status parent, status self, ...
                if (backgroundCheckTypesThatCanTriggerThisType?.length && config && quota && backgroundCheckTestConfig) {
                    backgroundCheckTypesThatCanTriggerThisType.forEach((bcType) => {
                        const completeClearStatus: BackgroundCheckStatus = 'completeClear';

                        const validBackgroundCheckTypeThatCanTrigger = accountBcs.rows.find(
                            (backgroundCheck) =>
                                backgroundCheck.columns.status === completeClearStatus && backgroundCheck.columns.type === bcType,
                        );

                        if (validBackgroundCheckTypeThatCanTrigger) {
                            const quotaByType = quota[params.data.type];
                            const validQuotaCount = quotaByType.quota > countRequestsByType(context, params.data.type);

                            if (!quotaByType.enabled || (quotaByType.enabled && validQuotaCount)) {
                                const backgroundCheckColumns: BackgroundCheckColumns = {
                                    ...params.data,
                                    requestedBy: params.data.requestedBy ?? 'System',
                                    updatedAt: params.data.updatedAt ?? Date.now(),
                                    consent: Boolean(params.data.consent),
                                };

                                requestBackgroundCheckScreening(context, {
                                    requestedBy: params.data.requestedBy ?? 'System',
                                    accountId: params.data.accountId,
                                    accountData: accountData.data,
                                    backgroundCheckType: params.data.type,
                                    config,
                                    backgroundCheckTestConfig,
                                    isTestMode,
                                    backgroundCheck: backgroundCheckColumns,
                                    quota,
                                });
                            }
                        }
                    });
                }
            }
        }
    },
    onAuthPost: (_appState, userState, params, context) => {
        // During client migration, we need to bypass standard authorization checks to manage data as needed.
        // This is only permitted for the administrator account.
        if (userState.email === administratorAccountEmail) {
            return params.action === 'delete' ? params.prevData : params.data;
        }

        const backgroundCheckData = params.action === 'create' ? params.data : params.prevData;

        if (
            !requireManageAccountsAndAnySystemPermission(
                context,
                {
                    userState,
                    accountIds: [backgroundCheckData.accountId],
                },
                'request-background-check',
            )
        ) {
            throw new Error('Unauthorized');
        }

        if (params.action === 'create' || params.action === 'update') {
            delete params.data.internalBackgroundCheckChange;
        }

        if (params.action === 'update') {
            params.data.status = params.prevData.status; // status can only be managed through platform functions
            params.data.accountId = params.prevData.accountId;
        }

        if (params.action === 'create' || params.action === 'update') {
            params.data.requestedBy = userState.displayName;
            return params.data;
        }

        return undefined;
    },
    onTransform: (context, params) => {
        const accountStorage = getExtendedAccountStorage(context);
        const accountData = accountStorage.get(params.data.accountId);

        const internalChange = params.data.internalBackgroundCheckChange;

        if (params.action === 'create' && !internalChange) {
            const { backgroundCheck: backgroundCheckData } = recalculateBackgroundCheckStatus(context, {
                backgroundCheck: {
                    ...params.data,
                    requestedBy: params.data.requestedBy ?? 'System',
                    updatedAt: params.data.updatedAt ?? 0,
                    consent: Boolean(params.data.consent),
                },
                accountData: accountData.data,
                skipHistoryCreation: false,
            });

            params.data = { ...params.data, ...backgroundCheckData };
        }

        if (internalChange) {
            if (internalChange.isManualChange) {
                params.data.type = internalChange.type;
                params.data.status = internalChange.status;
                params.data.updatedAt = internalChange.updatedAt;
            } else if (params.data.type === internalChange.type) {
                params.data.status = internalChange.status;
                params.data.updatedAt = internalChange.updatedAt;
            }

            delete params.data.internalBackgroundCheckChange;
        } else {
            if (params.action === 'update') {
                const handleStatusList: Array<BackgroundCheckStatus> = ['didNotConsent', 'formIncomplete', 'readyToSubmit'];

                if (
                    handleStatusList.includes(params.data.status as BackgroundCheckStatus) ||
                    params.prevData.consent !== params.data.consent
                ) {
                    const { backgroundCheck: backgroundCheckData } = recalculateBackgroundCheckStatus(context, {
                        backgroundCheck: {
                            ...params.data,
                            requestedBy: params.data.requestedBy ?? 'System',
                            updatedAt: params.data.updatedAt ?? 0,
                            consent: Boolean(params.data.consent),
                        },
                        accountData: accountData.data,
                        skipHistoryCreation: false,
                    });

                    params.data = { ...params.data, ...backgroundCheckData };
                }
            }
        }

        return {
            data: params.data,
            columns: {
                requestedBy: params.data.requestedBy ?? 'System',
                accountId: params.data.accountId,
                status: params.data.status,
                type: params.data.type,
                updatedAt: params.data.updatedAt ?? Date.now(),
                consent: Boolean(params.data.consent),
            },
        };
    },
    onAuthMassDelete: (_appState, userState) => {
        if (!requireAnySystemPermission(userState, 'manage-background-check-data')) {
            throw new AuthError('Unauthorized');
        }
    },
});
